import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {animate, keyframes, state, style, transition, trigger} from '@angular/animations';
import {PieceJointe} from '../services/pieces-jointes/piece-jointe.model';
import {FileTypeEnum} from '../services';
import {PiecejointeService} from '../services/pieces-jointes/piecejointe.service';
import {FileSizePipe} from '@Common/widgets/pipes/file-size';
import {SnackBarDataModel, SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import {ErrorUpload} from './error.interface';
import {SafeHtml} from '@angular/platform-browser';
import {MatDialog} from '@angular/material/dialog';
import {DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {ModelPjViewModalComponent} from '../model-pj/model-pj-view-modal/model-pj-view-modal.component';
import {DocumentService} from '@Services/common/document.service';

/**
 * Upload d'une pièce jointe
 * Une seule PJ à la fois
 */
@Component({
  selector: 'dar-piece-jointe-uploader',
  templateUrl: './piece-jointe-uploader.component.html',
  styleUrls: ['./piece-jointe-uploader.component.css'],
  animations: [
    trigger('flyInOut', [
      state('toto', style({transform: 'translateX(0)'})),
      transition('void => *', [
        animate(1000, keyframes([
          style({opacity: 0, transform: 'translateX(-100%)', offset: 0}),
          style({opacity: 1, transform: 'translateX(15px)', offset: 0.3}),
          style({opacity: 1, transform: 'translateX(0)', offset: 1.0})
        ]))
      ]),
      transition('* => void', [
        animate(300, keyframes([
          style({opacity: 1, transform: 'translateX(0)', offset: 0}),
          style({opacity: 1, transform: 'translateX(-15px)', offset: 0.7}),
          style({opacity: 0, transform: 'translateX(100%)', offset: 1.0})
        ]))
      ])
    ])
  ]
})
export class PieceJointeUploaderComponent implements OnInit, OnDestroy {
  @Input() typeFile: FileTypeEnum = null;

  @Input()
  disabled: boolean;

  @Input()
  accept: string;

  @Input()
  multiple = false;

  @Input()
  maxFileSize: number;

  @Input()
  connector: Subject<any>;

  @Input()
  placeHolder: SafeHtml = 'Sélectionner une pièce jointe';

  @Input() displayModelFiles = false;

  @Output()
  onFinished: EventEmitter<PieceJointe[]> = new EventEmitter<PieceJointe[]>();

  @Output()
  onError: EventEmitter<ErrorUpload> = new EventEmitter<ErrorUpload>();

  @Output()
  onSelected: EventEmitter<PieceJointe> = new EventEmitter<PieceJointe>();

  piecesJointes: PieceJointe[] = [];
  piecesUploaded: PieceJointe[] = [];
  subscriptionSignal: Subscription;

  launcher: Subject<any> = new Subject<any>();
  collector: number;

  files: FileList;

  state = '';
  sent = false;

  subs: Subscription[] = [];

  warning = false;
  error = false;
  succes = false;

  tooltip: string;

  warningColor = '#FFCC00';
  errorColor = 'red';
  succesColor = 'lightblue';

  @ViewChild('fileInput', { static: true }) fileInput;

  constructor(@Inject(LOCALE_ID) private locale: string,
              public dialog: MatDialog,
              private docSvc: DocumentService,
              private snackSvc: SnackBarToastyService,
              private pieceJointeService: PiecejointeService) {}

  ngOnInit() {
    this.subs.push(this.connector.subscribe(objectToAtach => this.startUpload(objectToAtach)));
  }

  private startUpload(objectToAtach: PieceJointe) {
    objectToAtach.type_file = this.typeFile;

    this.collector = 0;

    if (this.files) {
      if (this.checkFileSize(objectToAtach)) {
        this.uploadFile(objectToAtach);
      } else {
        this.toasty('La taille du fichier est trop grande, elle excède 5 Mo', '', typeToast.error);
        this.state = this.warningColor;
        this.error = true;

        const error = <ErrorUpload>{
          pj: objectToAtach,
          error: 'Taille trop grande',
          codeError: 1
        };


        this.onError.emit(error);
        this.raz();
      }
    } else {
      this.annouceParent();
    }
  }

  /**
   * Exécution du toaster
   * @param msg
   * @param title
   * @param type
   */
  public toasty(msg: string, title: string = '', type: typeToast = typeToast.success) {
    const config: SnackBarDataModel = {
      title: title,
      message: msg,
      type: type,
      showClose: true
    };

    this.snackSvc.openSnackToasty(config);
  }

  public canAddMoreFiles() {
    if (this.disabled) {
      return false;
    }

    return !(!this.multiple && this.piecesJointes.length >= 1);
  }
  public openModels() {
    const [canvasHeight, canvasWidth] = this.docSvc.getScreenSize();
    const dialogRef = this.dialog.open(ModelPjViewModalComponent);
    dialogRef.updateSize(`${canvasWidth - 200}px`, `${canvasHeight - 170}px`);
    dialogRef.updatePosition({top: '50px'});
  }

  checkFileSize(pieceJointe: PieceJointe) {
    let maxSize = false;

    if (this.maxFileSize) {
      this.warning = this.maxFileSize < pieceJointe.file.size;
    }

    if (this.warning) {
      const fileSize: FileSizePipe = new FileSizePipe(this.locale);

      // this.state = this.warningColor;
      this.tooltip  = `Le fichier choici est trop grand, la taille maximale supportée est : ${fileSize.transform(this.maxFileSize, 2, this.locale)},
      le fichier sélectionné est à ${fileSize.transform(pieceJointe.file.size, 2, this.locale)}, veuillez téléverser un fichier plus petit.`;

      maxSize = true;
    }

    return ! maxSize;
  }

  preparePJUpload(event: Event) {
    this.files = this.fileInput.nativeElement.files;
    for (let i = 0; i < this.files.length; i++) {
      this.piecesJointes.push(this.getPj(this.files.item(i)));
    }

    if (this.piecesJointes && this.piecesJointes.length > 0) {
      this.onSelected.emit(this.piecesJointes[0]);
    }
  }

  raz() {
    this.sent = false;
    this.piecesJointes = [];
    this.piecesUploaded = null;
    this.collector = 0;
    this.files = null;
  }

  getPj(file: any): PieceJointe {
    const _pj = <PieceJointe> {
      file: file,
      name: file.name
    };

    return _pj;
  }

  collectFiles(_pieceJointe) {
    this.collector++;

    if (_pieceJointe) {
      _pieceJointe.name = _pieceJointe.filename;
      this.piecesUploaded.push(_pieceJointe);
    }

    if (this.collector === this.piecesJointes.length) {
      this.annouceParent();
    }
  }

  private annouceParent(error = null) {
    this.onFinished.emit(this.piecesUploaded);
  }

  childDestroy(event) {
    const idx = this.piecesJointes.indexOf(event);
    if (idx !== -1) {
      this.piecesJointes.splice(idx, 1);
    }
  }

  launchUpload() {
    this.launcher.next();
  }

  uploadFile(objectToAtach) {
    if (this.sent) {
      return;
    }

    const pieceJointe: PieceJointe = objectToAtach;

    this.subs.push((this.pieceJointeService.upload(pieceJointe).subscribe(
      data => {
        this.doSucces(data);

      },
      error => {
        this.doError(error, objectToAtach);
      })));
  }

  doSucces(pieceJointe: PieceJointe): void {
    this.sent = true;
    this.onFinished.emit([pieceJointe]);
  }

  doError(error: any, pj: PieceJointe): void {
    const errorEmit = <ErrorUpload>{
      pj: pj,
      error: error,
      codeError: 2
    };

    this.state = this.warningColor;
    this.error = true;
    this.raz();

    this.onFinished.emit(null);
    this.onError.emit(errorEmit);
  }

  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((_sub) => {
        if (_sub) {
          _sub.unsubscribe();
        }
      });
    }
  }
}
